import { useEffect, useState } from 'react';
import { useNotifications } from '../../hooks/useNotifications';

export default function NotificationSetup({ familyMemberId }) {
  const { 
    permission, 
    token, 
    loading, 
    requestPermission, 
    setupForegroundListener,
    isSupported 
  } = useNotifications();
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Don't show prompt if:
    // - Not supported
    // - Permission already granted/denied
    // - User has existing token
    // - User previously dismissed
    const hasToken = !!token;
    const userDismissed = localStorage.getItem('papersedge-notifications-dismissed') === 'true';
    
    if (isSupported && permission === 'default' && !hasToken && !userDismissed) {
      setShowPrompt(true);
    } else {
      setShowPrompt(false);
    }
  }, [isSupported, permission, token]);

  useEffect(() => {
    if (permission === 'granted') {
      const unsubscribe = setupForegroundListener((payload) => {
        setNotifications(prev => [
          {
            id: Date.now(),
            title: payload.notification.title,
            body: payload.notification.body,
            data: payload.data,
            timestamp: new Date()
          },
          ...prev.slice(0, 4)
        ]);
      });

      return unsubscribe;
    }
  }, [permission, setupForegroundListener]);

  const handleEnableNotifications = async () => {
    const success = await requestPermission();
    if (success) {
      setShowPrompt(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <>
      {showPrompt && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                🔔
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-1">
                Stay updated with family notifications
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Get instant updates about tasks, allowances, messages, and family events.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleEnableNotifications}
                  disabled={loading}
                  className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                >
                  {loading ? 'Enabling...' : 'Enable Notifications'}
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('papersedge-notifications-dismissed', 'true');
                    setShowPrompt(false);
                  }}
                  className="text-slate-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-100"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-white border border-slate-200 rounded-lg shadow-lg p-4 max-w-sm animate-slide-in"
            >
              <h4 className="font-semibold text-slate-900 mb-1">
                {notification.title}
              </h4>
              <p className="text-slate-600 text-sm">
                {notification.body}
              </p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}